import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import { ValueAiSettingCard } from '@/components/prioritization/values/ValueAiSettingCard';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Layers,
  Ruler,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
} from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useCanDefineAttributes, DEFINE_ATTRIBUTES_NEEDS } from '../../../hooks/prioritization/useCanDefineAttributes';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import type { Attribute, MeasurementScale, MeasurementPoint, MeasurementScaleType } from '../../../types/prioritization/lifecycle';

// ============================================================================
// Helpers
// ============================================================================

function generateId(): string {
  return crypto.randomUUID?.() ?? `scale-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const SCALE_TYPE_LABELS: Record<MeasurementScaleType, string> = {
  numeric: 'Numeric Range',
  ordinal: 'Ordinal Labels',
  binary: 'Binary (Yes/No)',
  percentage: 'Percentage',
  currency: 'Currency',
  fibonacci: 'Fibonacci',
  custom: 'Custom',
};

const SCALE_PRESETS: { label: string; scale: Omit<MeasurementScale, 'id'> }[] = [
  {
    label: 'Low / Medium / High',
    scale: {
      label: 'Low / Medium / High',
      scaleType: 'ordinal',
      points: [
        { value: 1, label: 'Low', color: '#22c55e' },
        { value: 2, label: 'Medium', color: '#f59e0b' },
        { value: 3, label: 'High', color: '#ef4444' },
      ],
    },
  },
  {
    label: '1–5 Numeric',
    scale: {
      label: '1–5 Scale',
      scaleType: 'numeric',
      min: 1,
      max: 5,
      step: 1,
      points: [1, 2, 3, 4, 5].map((v) => ({ value: v, label: String(v) })),
    },
  },
  {
    label: '1–10 Numeric',
    scale: {
      label: '1–10 Scale',
      scaleType: 'numeric',
      min: 1,
      max: 10,
      step: 1,
      points: Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
    },
  },
  {
    label: 'T-Shirt Sizes',
    scale: {
      label: 'T-Shirt Sizes',
      scaleType: 'ordinal',
      points: [
        { value: 1, label: 'XS' },
        { value: 2, label: 'S' },
        { value: 3, label: 'M' },
        { value: 4, label: 'L' },
        { value: 5, label: 'XL' },
      ],
    },
  },
  {
    label: 'Fibonacci (Planning Poker)',
    scale: {
      label: 'Fibonacci',
      scaleType: 'fibonacci',
      points: [1, 2, 3, 5, 8, 13, 21].map((v) => ({ value: v, label: String(v) })),
    },
  },
  {
    label: 'MoSCoW',
    scale: {
      label: 'MoSCoW',
      scaleType: 'ordinal',
      points: [
        { value: 4, label: 'Must Have', color: '#dc2626' },
        { value: 3, label: 'Should Have', color: '#f59e0b' },
        { value: 2, label: 'Could Have', color: '#3b82f6' },
        { value: 1, label: "Won't Have", color: '#6b7280' },
      ],
    },
  },
  {
    label: 'Yes / No',
    scale: {
      label: 'Yes / No',
      scaleType: 'binary',
      points: [
        { value: 1, label: 'Yes', color: '#22c55e' },
        { value: 0, label: 'No', color: '#ef4444' },
      ],
    },
  },
];

// ============================================================================
// Scale Editor Sub-Component
// ============================================================================

function ScaleEditor({
  scale,
  isDefault,
  onUpdate,
  onDelete,
  onSetDefault,
}: {
  scale: MeasurementScale;
  isDefault: boolean;
  onUpdate: (updated: MeasurementScale) => void;
  onDelete: () => void;
  onSetDefault: () => void;
}) {
  const [expanded, setExpanded] = useState(true);

  const addPoint = () => {
    const nextValue = scale.points.length > 0 ? Math.max(...scale.points.map((p) => p.value)) + 1 : 1;
    onUpdate({
      ...scale,
      points: [...scale.points, { value: nextValue, label: '' }],
    });
  };

  const updatePoint = (index: number, updates: Partial<MeasurementPoint>) => {
    const points = scale.points.map((p, i) => (i === index ? { ...p, ...updates } : p));
    onUpdate({ ...scale, points });
  };

  const removePoint = (index: number) => {
    onUpdate({ ...scale, points: scale.points.filter((_, i) => i !== index) });
  };

  return (
    <div className={`border  ${isDefault ? 'border-primary-300 bg-primary-50/30' : 'border-[var(--rule)]'}`}>
      {/* Scale Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--surface)]"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-[var(--text-pri)]">{scale.label || 'Untitled Scale'}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
                {SCALE_TYPE_LABELS[scale.scaleType]}
              </span>
              {isDefault && (
                <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 font-medium">
                  Default
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {scale.points.slice(0, 6).map((p, i) => (
                <span
                  key={i}
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: p.color ? `${p.color}20` : '#f3f4f6', color: p.color || '#374151' }}
                >
                  {p.label}
                </span>
              ))}
              {scale.points.length > 6 && <span className="text-xs text-[var(--text-ter)]">+{scale.points.length - 6}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {!isDefault && (
            <button onClick={onSetDefault} className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1 rounded hover:bg-primary-50" title="Set as default">
              Set Default
            </button>
          )}
          <button onClick={onDelete} className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1" title="Delete scale">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-[var(--rule)]">
          {/* Scale Name & Type */}
          <div className="grid grid-cols-2 gap-4 pt-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Scale Name</label>
              <input
                type="text"
                value={scale.label}
                onChange={(e) => onUpdate({ ...scale, label: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                placeholder="e.g., Low / Medium / High"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Scale Type</label>
              <select
                value={scale.scaleType}
                onChange={(e) => onUpdate({ ...scale, scaleType: e.target.value as MeasurementScaleType })}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm"
              >
                {Object.entries(SCALE_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description</label>
            <input
              type="text"
              value={scale.description || ''}
              onChange={(e) => onUpdate({ ...scale, description: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
              placeholder="Optional description of when to use this scale"
            />
          </div>

          {/* Points */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-[var(--text-sec)]">Values</label>
              <button onClick={addPoint} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800">
                <Plus className="h-3 w-3" /> Add Value
              </button>
            </div>
            <div className="space-y-2">
              {scale.points.map((point, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  <input
                    type="number"
                    value={point.value}
                    onChange={(e) => updatePoint(idx, { value: parseFloat(e.target.value) || 0 })}
                    className="w-16 px-2 py-1.5 border border-[var(--rule)] rounded text-sm text-center"
                    title="Numeric value"
                  />
                  <input
                    type="text"
                    value={point.label}
                    onChange={(e) => updatePoint(idx, { label: e.target.value })}
                    className="flex-1 px-2 py-1.5 border border-[var(--rule)] rounded text-sm"
                    placeholder="Label"
                  />
                  <input
                    type="color"
                    value={point.color || '#6b7280'}
                    onChange={(e) => updatePoint(idx, { color: e.target.value })}
                    className="w-8 h-8 p-0.5 border border-[var(--rule)] rounded cursor-pointer"
                    title="Color"
                  />
                  <input
                    type="text"
                    value={point.description || ''}
                    onChange={(e) => updatePoint(idx, { description: e.target.value })}
                    className="flex-1 px-2 py-1.5 border border-[var(--rule)] rounded text-sm text-[var(--text-ter)]"
                    placeholder="Description (optional)"
                  />
                  <button onClick={() => removePoint(idx)} className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1 flex-shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function AttributeDetail() {
  const { attributeKey } = useParams<{ attributeKey: string }>();
  const navigate = useNavigate();
  const scalesOnly = !!useMatch({ path: '/prioritization/settings/value-scales/:attributeKey', end: true });
  const addNotification = useUIStore((state) => state.addNotification);

  const attribute = useAttributesStore((state) => state.attributes.find((a) => a.key === attributeKey));
  const updateAttribute = useAttributesStore((state) => state.updateAttribute);
  // Renaming a dimension or changing its scales and anchors is the workspace's
  // call; the page shows it to everyone and lets only those who may change it
  // edit (build contract §2.8; roadmap 1C V-10).
  const canEdit = useCanDefineAttributes();

  // Local draft state so we can batch-save
  const [draft, setDraft] = useState<Partial<Attribute>>(() => ({
    label: attribute?.label,
    description: attribute?.description,
    required: attribute?.required,
    type: attribute?.type,
    ownerId: attribute?.ownerId,
    ownerName: attribute?.ownerName,
    scales: attribute?.scales ?? [],
    defaultScaleId: attribute?.defaultScaleId,
    semanticType: attribute?.semanticType,
    contextScope: attribute?.contextScope ?? 'global',
    inheritable: attribute?.inheritable ?? true,
    overridable: attribute?.overridable ?? true,
  }));

  const [dirty, setDirty] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [expandedScaleId, setExpandedScaleId] = useState<string | null>(null);

  const scales = useMemo(() => (draft.scales ?? []) as MeasurementScale[], [draft.scales]);

  const patch = useCallback(
    (updates: Partial<Attribute>) => {
      setDraft((prev) => ({ ...prev, ...updates }));
      setDirty(true);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!attributeKey) return;
    const result = await updateAttribute(attributeKey, draft);
    if (!result?.error) {
      setDirty(false);
      addNotification({ type: 'success', title: 'Saved', message: `Attribute "${draft.label}" updated`, duration: 3000 });
    }
    // On error, store has already rolled back local state and dispatched a visible toast via useStoreErrorHandler
  }, [attributeKey, draft, updateAttribute, addNotification]);

  const addScale = useCallback(
    (preset?: Omit<MeasurementScale, 'id'>) => {
      const newScale: MeasurementScale = {
        id: generateId(),
        label: preset?.label ?? 'New Scale',
        scaleType: preset?.scaleType ?? 'ordinal',
        points: preset?.points ?? [],
        min: preset?.min,
        max: preset?.max,
        step: preset?.step,
        isDefault: scales.length === 0,
      };
      const updated = [...scales, newScale];
      patch({
        scales: updated,
        defaultScaleId: updated.length === 1 ? newScale.id : draft.defaultScaleId,
      });
      setShowPresets(false);
    },
    [scales, draft.defaultScaleId, patch],
  );

  const updateScale = useCallback(
    (scaleId: string, updated: MeasurementScale) => {
      patch({ scales: scales.map((s) => (s.id === scaleId ? updated : s)) });
    },
    [scales, patch],
  );

  const deleteScale = useCallback(
    (scaleId: string) => {
      const remaining = scales.filter((s) => s.id !== scaleId);
      const newDefault = draft.defaultScaleId === scaleId ? remaining[0]?.id : draft.defaultScaleId;
      patch({ scales: remaining, defaultScaleId: newDefault });
    },
    [scales, draft.defaultScaleId, patch],
  );

  if (!attribute) {
    return (
      <div className="p-8 text-center">
        <p className="text-[var(--text-ter)]">Attribute "{attributeKey}" not found.</p>
        <button
          onClick={() =>
            navigate(scalesOnly ? '/prioritization/settings/value-scales' : '/prioritization/settings/attributes')
          }
          className="mt-4 btn btn-secondary"
        >
          {scalesOnly ? 'Back to value scales' : 'Back to Attributes'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              navigate(scalesOnly ? '/prioritization/settings/value-scales' : '/prioritization/settings/attributes')
            }
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            title={scalesOnly ? 'Back to value scales' : 'Back to attributes'}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">
              {scalesOnly ? `Measurement scales — ${attribute.label}` : attribute.label}
            </h1>
            <p className="text-sm text-[var(--text-ter)] font-mono">{attribute.key}</p>
            {scalesOnly && (
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Edit scales for this attribute. For label, type, and ownership, open the full attribute page from{' '}
                <button
                  type="button"
                  onClick={() => navigate(`/prioritization/settings/attributes/${attribute.key}`)}
                  className="text-primary-600 hover:text-primary-800 underline font-medium"
                >
                  Settings → Attributes
                </button>
                .
              </p>
            )}
          </div>
        </div>
        {canEdit ? (
        <button
          onClick={handleSave}
          disabled={!dirty}
          className={`flex items-center gap-2 px-4 py-2  font-medium text-sm transition-colors ${
            dirty
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          <Save className="h-4 w-4" />
          {dirty ? 'Save Changes' : 'Saved'}
        </button>
        ) : (
          <p className="max-w-xs text-right text-xs text-[var(--text-ter)]">{DEFINE_ATTRIBUTES_NEEDS}</p>
        )}
      </div>

      <fieldset disabled={!canEdit} className="space-y-6 min-w-0 border-0 p-0 m-0">

      {/* General Settings */}
      {!scalesOnly && (
      <div className="card">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">General</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Label</label>
            <input
              type="text"
              value={draft.label ?? ''}
              onChange={(e) => patch({ label: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Semantic Type</label>
            <select
              value={draft.semanticType ?? ''}
              onChange={(e) => patch({ semanticType: (e.target.value || undefined) as Attribute['semanticType'] })}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm"
            >
              <option value="">— None —</option>
              <option value="evaluative">Evaluative (scoring/ranking)</option>
              <option value="descriptive">Descriptive (context)</option>
              <option value="ownership">Ownership (responsibility)</option>
              <option value="temporal">Temporal (time-related)</option>
              <option value="evidence">Evidence (supporting rationale)</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
            <input
              type="text"
              value={draft.description ?? ''}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="What this attribute measures"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
            />
          </div>
          <div className="flex items-center gap-4 col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.required ?? false}
                onChange={(e) => patch({ required: e.target.checked })}
                className="rounded border-[var(--rule)]"
              />
              <span className="text-sm text-[var(--text-sec)]">Required</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.inheritable ?? true}
                onChange={(e) => patch({ inheritable: e.target.checked })}
                className="rounded border-[var(--rule)]"
              />
              <span className="text-sm text-[var(--text-sec)]">Inheritable to child scopes</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.overridable ?? true}
                onChange={(e) => patch({ overridable: e.target.checked })}
                className="rounded border-[var(--rule)]"
              />
              <span className="text-sm text-[var(--text-sec)]">Overridable by child scopes</span>
            </label>
          </div>
        </div>
      </div>
      )}

      {/* Measurement Scales — table like Attribute Schema (KEY, LABEL, TYPE, VALUES, DEFAULT, ACTIONS) */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">Measurement Scales</h2>
              <p className="text-sm text-[var(--text-ter)]">
                Define how this attribute can be measured. Different cycles, tracks, or sessions can choose which scale to use.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowPresets(!showPresets)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                <Copy className="h-4 w-4" />
                From Preset
              </button>
              {showPresets && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg z-10">
                  {SCALE_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => addScale(preset.scale)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-[var(--surface)] first: last: flex items-center justify-between"
                    >
                      <span>{preset.label}</span>
                      <span className="text-xs text-[var(--text-ter)]">{preset.scale.points.length} values</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => addScale()}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" />
              Add Scale
            </button>
          </div>
        </div>

        {scales.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-[var(--rule)]">
            <Ruler className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-[var(--text-ter)] text-sm">No measurement scales defined yet.</p>
            <p className="text-[var(--text-ter)] text-xs mt-1">Add a scale from a preset or create a custom one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Values</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Default</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
                {scales.map((scale) => {
                  const isDefault = scale.id === draft.defaultScaleId;
                  const valuesSummary = scale.points.length === 0
                    ? '—'
                    : scale.points.length <= 4
                      ? scale.points.map((p) => p.label).join(', ')
                      : `${scale.points.length} values`;
                  return (
                    <React.Fragment key={scale.id}>
                      <tr
                        key={scale.id}
                        className={expandedScaleId === scale.id ? 'bg-primary-50/50' : 'hover:bg-[var(--surface)]'}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-[var(--text-sec)]">{scale.id.slice(0, 8)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)]">{scale.label || 'Untitled Scale'}</td>
                        <td className="px-4 py-3 text-sm text-[var(--text-sec)]">{SCALE_TYPE_LABELS[scale.scaleType]}</td>
                        <td className="px-4 py-3 text-sm text-[var(--text-sec)]">{valuesSummary}</td>
                        <td className="px-4 py-3 text-sm">
                          {isDefault ? (
                            <span className="text-primary-700 font-medium">Default</span>
                          ) : (
                            <span className="text-[var(--text-ter)]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!isDefault && (
                              <button
                                onClick={() => patch({ defaultScaleId: scale.id })}
                                className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1 rounded hover:bg-primary-50"
                                title="Set as default"
                              >
                                Set default
                              </button>
                            )}
                            <button
                              onClick={() => setExpandedScaleId((id) => (id === scale.id ? null : scale.id))}
                              className="p-1.5 text-[var(--text-ter)] hover:text-primary-600 rounded"
                              title="Edit scale"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteScale(scale.id)}
                              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] rounded"
                              title="Delete scale"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedScaleId === scale.id && (
                        <tr key={`${scale.id}-editor`}>
                          <td colSpan={6} className="px-4 py-3 bg-[var(--surface)] border-t border-[var(--rule)]">
                            <ScaleEditor
                              scale={scale}
                              isDefault={isDefault}
                              onUpdate={(updated) => updateScale(scale.id, updated)}
                              onDelete={() => { deleteScale(scale.id); setExpandedScaleId(null); }}
                              onSetDefault={() => patch({ defaultScaleId: scale.id })}
                            />
                            <div className="mt-2 flex justify-end">
                              <button
                                onClick={() => setExpandedScaleId(null)}
                                className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1.5 rounded border border-[var(--rule)] hover:bg-[var(--surface)]"
                              >
                                Done
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AI for this value (migration 920) */}
      {!scalesOnly && attributeKey && (
        <ValueAiSettingCard attributeKey={attributeKey} attributeLabel={attribute?.label ?? attributeKey} />
      )}

      {/* Inheritance Preview */}
      {!scalesOnly && (
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-5 w-5 text-[var(--text-ter)]" />
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Inheritance</h2>
        </div>
        <p className="text-sm text-[var(--text-ter)] mb-4">
          Scales defined here are available at every level. Cycles, tracks, and sessions can choose which scale to use for this attribute, inheriting from the level above if not overridden.
        </p>
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <span className="font-medium text-[var(--text-sec)]">Global</span>
            {draft.defaultScaleId && scales.find((s) => s.id === draft.defaultScaleId) && (
              <span className="text-xs text-[var(--text-sec)]">
                ({scales.find((s) => s.id === draft.defaultScaleId)!.label})
              </span>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          <div className="px-3 py-2 bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] font-medium">
            Cycle
            <span className="text-xs text-[var(--text-sec)] ml-1">can override</span>
          </div>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          <div className="px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
            Track
            <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] ml-1">can override</span>
          </div>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          <div className="px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] font-medium">
            Session
            <span className="text-xs text-[var(--mustard)] ml-1">can override</span>
          </div>
        </div>
        {!(draft.overridable ?? true) && (
          <p className="mt-3 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2 rounded">
            Overriding is disabled — child scopes must use the global default scale.
          </p>
        )}
      </div>
      )}
      </fieldset>
    </div>
  );
}
